import React, { useState, useEffect } from 'react';
import { Shield, ChevronDown, ChevronUp, ExternalLink, Cookie } from 'lucide-react';
import { useCookieConsent } from '../contexts/CookieConsentContext';

const CATEGORIES = [
  {
    id: 'necessary',
    label: 'Strictly Necessary',
    description: 'Required for the site to function. Includes authentication tokens, session management, and security features. These cannot be disabled.',
    locked: true,
    cookies: ['Session ID', 'Auth token (JWT)', 'CSRF protection'],
  },
  {
    id: 'preferences',
    label: 'Functional / Preferences',
    description: 'Remember your settings like language, theme, and display preferences so you don\'t have to set them every time you visit.',
    locked: false,
    cookies: ['Language preference', 'Theme settings', 'UI state'],
  },
  {
    id: 'analytics',
    label: 'Analytics & Performance',
    description: 'Help us understand how visitors interact with Stilos by collecting anonymized usage data. This helps us improve the product for everyone.',
    locked: false,
    cookies: ['Google Analytics (GA4)', 'Internal usage metrics', 'Error reporting'],
  },
  {
    id: 'marketing',
    label: 'Marketing & Advertising',
    description: 'Used to deliver relevant advertisements and track the effectiveness of marketing campaigns. These may be set by third-party advertising partners.',
    locked: false,
    cookies: ['Conversion tracking', 'Retargeting pixels', 'Social media trackers'],
  },
];

const CookieFab = () => {
  const { hasConsented, openPreferences } = useCookieConsent();
  
  if (!hasConsented) return null;

  return (
    <button
      onClick={openPreferences}
      className="fixed bottom-5 left-5 z-[9998] w-10 h-10 rounded-full bg-[var(--color-charcoal)] text-white shadow-lg flex items-center justify-center hover:bg-[var(--color-charcoal-light)] transition-all duration-200 hover:scale-110 group"
      data-testid="cookie-fab-btn"
      aria-label="Cookie preferences"
      title="Manage cookie preferences"
    >
      <Cookie className="w-4 h-4" />
      <span className="absolute left-12 bg-[var(--color-charcoal)] text-white text-[10px] font-medium px-2.5 py-1.5 rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none shadow-lg">
        Cookie Settings
      </span>
    </button>
  );
};

const CookieBanner = () => {
  const {
    consent, showBanner, showPreferences, setShowPreferences,
    acceptAll, rejectAll, updateConsent, setShowBanner,
  } = useCookieConsent();

  const [localConsent, setLocalConsent] = useState(consent);
  const [expanded, setExpanded] = useState(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => { setLocalConsent(consent); }, [consent]);

  useEffect(() => {
    if (showBanner) {
      const t = setTimeout(() => setVisible(true), 50);
      return () => clearTimeout(t);
    } else {
      setVisible(false);
    }
  }, [showBanner]);

  if (!showBanner) return (
    <CookieFab />
  );

  const handleToggle = (id) => {
    if (id === 'necessary') return;
    setLocalConsent(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const handleSavePreferences = () => {
    updateConsent(localConsent);
  };

  const enabledCount = Object.entries(localConsent).filter(([k, v]) => v && k !== 'necessary').length;

  return (
    <div
      className={`fixed inset-0 z-[9999] flex items-end sm:items-end justify-center transition-opacity duration-500 ${visible ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
      data-testid="cookie-banner-overlay"
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/20 backdrop-blur-[2px]" />

      {/* Banner */}
      <div
        className={`relative w-full max-w-2xl mx-4 mb-4 sm:mb-6 transform transition-all duration-500 ease-out ${visible ? 'translate-y-0' : 'translate-y-8'}`}
        data-testid="cookie-banner"
      >
        <div className="bg-[var(--color-surface-elevated)] border border-[var(--color-border)] rounded-2xl shadow-2xl overflow-hidden">
          
          {/* Header */}
          <div className="p-5 pb-0">
            <div className="flex items-center gap-2.5 mb-3">
              <div className="w-8 h-8 rounded-xl bg-[var(--color-charcoal)] flex items-center justify-center">
                <Shield className="w-4 h-4 text-white" />
              </div>
              <div>
                <h3 className="font-display text-sm font-semibold text-[var(--color-charcoal)]" data-testid="cookie-banner-title">
                  Your Privacy Matters
                </h3>
                <p className="text-[10px] text-[var(--color-muted)]">GDPR &amp; ePrivacy Compliant</p>
              </div>
            </div>
            <p className="text-xs text-[var(--color-muted)] leading-relaxed">
              We use cookies to ensure Stilos works properly and to improve your experience. You choose which optional cookies to allow. 
              Read our{' '}
              <a href="/cookies" className="text-[var(--color-coral)] hover:underline inline-flex items-center gap-0.5">
                Cookie Policy <ExternalLink className="w-2.5 h-2.5" />
              </a>{' '}
              and{' '}
              <a href="/privacy" className="text-[var(--color-coral)] hover:underline inline-flex items-center gap-0.5">
                Privacy Policy <ExternalLink className="w-2.5 h-2.5" />
              </a>
              .
            </p>
          </div>

          {/* Preferences Panel (expandable) */}
          {showPreferences && (
            <div className="px-5 pt-4 space-y-2" data-testid="cookie-preferences-panel">
              {CATEGORIES.map((cat) => (
                <div key={cat.id} className="border border-[var(--color-border)] rounded-xl overflow-hidden bg-[var(--color-bg)]">
                  <div
                    className="flex items-center justify-between p-3 cursor-pointer select-none"
                    onClick={() => setExpanded(expanded === cat.id ? null : cat.id)}
                    data-testid={`cookie-category-${cat.id}`}
                  >
                    <div className="flex items-center gap-3">
                      {/* Toggle */}
                      <button
                        onClick={(e) => { e.stopPropagation(); handleToggle(cat.id); }}
                        disabled={cat.locked}
                        className={`relative w-9 h-5 rounded-full transition-colors duration-200 ${
                          localConsent[cat.id]
                            ? cat.locked ? 'bg-[var(--color-charcoal)]' : 'bg-[var(--color-coral)]'
                            : 'bg-[var(--color-border)]'
                        } ${cat.locked ? 'cursor-not-allowed opacity-70' : 'cursor-pointer'}`}
                        data-testid={`cookie-toggle-${cat.id}`}
                        aria-label={`Toggle ${cat.label}`}
                      >
                        <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform duration-200 ${
                          localConsent[cat.id] ? 'translate-x-4' : 'translate-x-0'
                        }`} />
                      </button>
                      <span className="text-xs font-medium text-[var(--color-charcoal)]">{cat.label}</span>
                      {cat.locked && (
                        <span className="text-[9px] uppercase tracking-wider text-[var(--color-muted)] bg-[var(--color-surface)] px-1.5 py-0.5 rounded-md font-medium">
                          Always on
                        </span>
                      )}
                    </div>
                    {expanded === cat.id
                      ? <ChevronUp className="w-3.5 h-3.5 text-[var(--color-muted)]" />
                      : <ChevronDown className="w-3.5 h-3.5 text-[var(--color-muted)]" />}
                  </div>
                  {expanded === cat.id && (
                    <div className="px-3 pb-3 border-t border-[var(--color-border-subtle)]">
                      <p className="text-[11px] text-[var(--color-muted)] leading-relaxed mt-2 mb-2">{cat.description}</p>
                      <div className="flex flex-wrap gap-1.5">
                        {cat.cookies.map((c) => (
                          <span key={c} className="text-[9px] bg-[var(--color-surface)] text-[var(--color-muted)] px-2 py-0.5 rounded-md">
                            {c}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Actions */}
          <div className="p-5 pt-4">
            {!showPreferences ? (
              /* Simple view */
              <div className="flex flex-col sm:flex-row gap-2">
                <button
                  onClick={acceptAll}
                  className="flex-1 h-10 bg-[var(--color-charcoal)] text-white text-xs font-medium rounded-xl hover:bg-[var(--color-charcoal-light)] transition-colors"
                  data-testid="cookie-accept-all-btn"
                >
                  Accept All
                </button>
                <button
                  onClick={rejectAll}
                  className="flex-1 h-10 bg-[var(--color-surface)] text-[var(--color-charcoal)] text-xs font-medium rounded-xl border border-[var(--color-border)] hover:bg-[var(--color-border-subtle)] transition-colors"
                  data-testid="cookie-reject-all-btn"
                >
                  Necessary Only
                </button>
                <button
                  onClick={() => setShowPreferences(true)}
                  className="flex-1 h-10 text-[var(--color-muted)] text-xs font-medium rounded-xl hover:text-[var(--color-charcoal)] hover:bg-[var(--color-surface)] transition-colors"
                  data-testid="cookie-customize-btn"
                >
                  Customize
                </button>
              </div>
            ) : (
              /* Preferences view */
              <div className="flex flex-col sm:flex-row gap-2">
                <button
                  onClick={handleSavePreferences}
                  className="flex-1 h-10 bg-[var(--color-charcoal)] text-white text-xs font-medium rounded-xl hover:bg-[var(--color-charcoal-light)] transition-colors"
                  data-testid="cookie-save-preferences-btn"
                >
                  Save Preferences {enabledCount > 0 && `(${enabledCount} optional)`}
                </button>
                <button
                  onClick={acceptAll}
                  className="flex-1 h-10 bg-[var(--color-surface)] text-[var(--color-charcoal)] text-xs font-medium rounded-xl border border-[var(--color-border)] hover:bg-[var(--color-border-subtle)] transition-colors"
                  data-testid="cookie-accept-all-prefs-btn"
                >
                  Accept All
                </button>
              </div>
            )}

            {/* Legal footer */}
            <p className="text-[9px] text-[var(--color-muted)] text-center mt-3 leading-relaxed">
              By using Stilos you agree to our{' '}
              <a href="/terms" className="underline hover:text-[var(--color-charcoal)]">Terms</a>.
              Consent is stored locally and can be changed anytime via{' '}
              <a href="/cookies" className="underline hover:text-[var(--color-charcoal)]">Cookie Settings</a>.
              {' '}Compliant with EU GDPR Art. 6(1)(a), ePrivacy Directive Art. 5(3).
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieBanner;
